import { ApolloClient, InMemoryCache, gql } from '@apollo/client';

import * as ApolloTypes from '../graphqlTypes/types';
import { localStorageWithMemoryFallback } from '../localStorageWithMemoryFallback';

interface Context {
  client: ApolloClient<InMemoryCache>;
}

export const typeDefs = gql`
  extend type Query {
    isLoggedIn: Boolean
    currentAccountId: ID
  }

  extend type Mutation {
    # Save the current org id. Pass null to delete the saved value.
    setCurrentAccountId(currentAccountId: ID): ID
  }

  enum SubgraphInsightsListGroupByColumnMock {
    CLIENT_NAME
    CLIENT_VERSION
    OPERATION_ID
    OPERATION_NAME
    SUBGRAPH_ID
    SUBGRAPH_NAME
  }

  extend type GraphVariant {
    subgraphInsightsListMock(
      after: String
      before: String
      filter: SubgraphInsightsListFilterInputMock
      first: Int
      from: Timestamp!
      groupBy: [SubgraphInsightsListGroupByColumnMock!]
      last: Int
      orderBy: SubgraphInsightsListOrderByInputMock
      to: Timestamp!
      requireLatencyMetrics: Boolean! = false
    ): GraphVariantSubgraphInsightsListItemConnectionMock
  }

  enum SubgraphInsightsListOrderByColumnMock {
    FETCHES_WITH_ERRORS_COUNT
    FETCHES_WITH_ERROR_COUNT_PER_MIN
    FETCH_COUNT
    FETCH_COUNT_PER_MIN
    FETCH_ERROR_PERCENTAGE
    SERVICE_TIME_P50
    SERVICE_TIME_P90
    SERVICE_TIME_P95
    SERVICE_TIME_P99
    SUBGRAPH_ID
    SUBGRAPH_NAME
    TOTAL_DURATION_MS
  }

  type SubgraphInsightsListItemMock {
    clientName: String
    clientVersion: String
    fetchCount: Long!
    fetchCountPerMin: Float!
    fetchErrorPercentage: Float!
    fetchesWithErrorsCount: Long!
    fetchesWithErrorsCountPerMin: Float!
    operationId: ID
    operationName: String!
    operationType: OperationType
    serviceTimeP50Ms: Float
    serviceTimeP90Ms: Float
    serviceTimeP95Ms: Float
    serviceTimeP99Ms: Float
    subgraphId: ID
    subgraphName: String!
    totalDurationMs: Float
  }

  input SubgraphInsightsListOrderByInputMock {
    column: SubgraphInsightsListOrderByColumnMock!
    direction: Ordering!
  }

  input SubgraphInsightsListFilterInInputMock {
    clientName: [String]
    clientVersion: [String]
    subgraphId: [String]
    subgraphName: [String]
  }

  input SubgraphInsightsListFilterInputMock {
    clientName: String
    clientVersion: String
    in: SubgraphInsightsListFilterInInputMock
    or: [SubgraphInsightsListFilterInputMock!]
    search: String
    subgraphId: String
    subgraphName: String
  }

  type GraphVariantSubgraphInsightsListItemEdgeMock {
    cursor: String!
    node: SubgraphInsightsListItemMock
  }

  type GraphVariantSubgraphInsightsListItemConnectionMock {
    edges: [GraphVariantSubgraphInsightsListItemEdgeMock!]
    nodes: [SubgraphInsightsListItemMock!]
    pageInfo: PageInfo!
    totalCount: Int!
  }
`;

const nodes = Array.from({ length: 50 }, (_, index) => {
  const subgraphName = `subgraph-${index + 1}`;
  return {
    clientName: 'cool-client-name',
    clientVersion: 'v1.2.3',
    fetchCount: Math.floor(Math.random() * 1000) + 50,
    fetchCountPerMin: Math.floor(Math.random() * 500) + 10,
    fetchErrorPercentage: Math.random() * 20,
    fetchesWithErrorsCount: Math.floor(Math.random() * 100) + 1,
    fetchesWithErrorsCountPerMin: Math.random() * 5,
    operationId: 'cool-operation-id',
    operationName: 'CoolOperationName',
    operationType: ApolloTypes.OperationType.QUERY,
    serviceTimeP50Ms: Math.random() * 200 + 50,
    serviceTimeP90Ms: Math.random() * 400 + 100,
    serviceTimeP95Ms: Math.random() * 600 + 150,
    serviceTimeP99Ms: Math.random() * 1000 + 300,
    subgraphId: subgraphName,
    subgraphName,
    totalDurationMs: Math.random() * 300 + 100,
  };
});

export const resolvers = {
  Query: {
    currentAccountId() {
      return JSON.parse(
        localStorageWithMemoryFallback.getItem('engine:currentAccountId') ||
          'null',
      );
    },
  },
  GraphVariant: {
    subgraphInsightsListMock: () => {
      return {
        edges: [],
        nodes,
        pageInfo: {
          endCursor: 'eyJncmFwaElEIdGVPZmZzZXQZSIsImZpZWxkGaWx0ZXIiOm51bGx9===',
          hasNextPage: true,
          hasPreviousPage: false,
          startCursor: 'W4iOiJSRUZFUkVOQ0lOR19PUEVSQVRJT05fQ09VTlQiLCJvc',
        },
        totalCount: 50,
      };
    },
  },
  Mutation: {
    setCurrentAccountId: (
      _root: unknown,
      {
        currentAccountId = null,
      }: ApolloTypes.SetCurrentAccountIdMutationVariables,
      context: Context,
    ) => {
      context.client.writeQuery<ApolloTypes.ClientSchemaSetCurrentAccountIdCacheWriteQuery>(
        {
          query: gql`
            query ClientSchemaSetCurrentAccountIdCacheWriteQuery {
              currentAccountId @client
            }
          `,
          data: { currentAccountId },
        },
      );

      if (currentAccountId) {
        localStorageWithMemoryFallback.setItem(
          'engine:currentAccountId',
          JSON.stringify(currentAccountId),
        );
      } else {
        localStorageWithMemoryFallback.removeItem('engine:currentAccountId');
      }

      return currentAccountId;
    },
  },
};
